import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Tooltip,
  IconButton,
  CircularProgress,
  Modal,
  Typography,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  AppBar,
  TableCell,
  Box,
} from "@mui/material";
import { styled } from "@mui/system";
import CloseIcon from "@mui/icons-material/Close";
import CircleIcon from "@mui/icons-material/Circle";
import LanguageIcon from "@mui/icons-material/Language";
import SmartphoneIcon from "@mui/icons-material/Smartphone";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CallOutlinedIcon from "@mui/icons-material/CallOutlined";
import DoneAllOutlinedIcon from "@mui/icons-material/DoneAllOutlined";
import DirectionsWalkOutlinedIcon from "@mui/icons-material/DirectionsWalkOutlined";
import Followup from "./ActionComponent/Followup";
import EnqDetails from "./EnqDetails";
import { API_URL } from "../../Config/api";
import PackageFollowup from "./ActionComponent/PackageFollowup";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  border: "2px solid #000",
  pt: 2,
  px: 4,
  pb: 3,
  backgroundColor: "#f1f1f1",
};

const EnquiryCard = styled(Card)({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  marginTop: "10px",
  backgroundColor: "#f1f1f1",
  boxShadow: "4px 4px 10px 7px rgba(135, 135, 135, 0.05)",
  height: "54px",
  borderRadius: "10px",
  transition: "2s ease-in-out",
});

const PackageTable = ({
  selectedDate,
  selectedService,
  selectedCallerStatus,
}) => {
  const accessToken = localStorage.getItem("token");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [pkgEnq, setPkgEnq] = useState([]);
  const [loadingPkg, setLoadingPkg] = useState(true);

  // Followup Modals
  const [open, setOpen] = useState(false);
  const [enqID, setEnqID] = useState([]);
  const [preFollowup, setPreFollowup] = useState([]);
  const flag = 1;

  // Details Modal
  const [openDetails, setOpenDeatils] = useState(false);
  const [evntId, setEvntId] = useState("");

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const handleOpenDetails = () => setOpenDeatils(true);
  const handleCloseDetails = () => setOpenDeatils(false);

  // Fetch Package Data
  useEffect(() => {
    const getPackageEnquiries = async () => {
      setLoadingPkg(true);
      try {
        const dateQuery = selectedDate
          ? `?date=${encodeURIComponent(selectedDate)}`
          : "";
        const res = await fetch(
          `${API_URL}/packages/package_srv_enquiry/0/${dateQuery}`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
          },
        );
        const data = await res.json();
        console.log("Package Enquiries:", data);
        if (data.detail === "No matching records found") {
          setPkgEnq([]);
        } else {
          setPkgEnq(data);
        }
      } catch (error) {
        console.error("Error fetching Package Enquiries:", error);
      } finally {
        setLoadingPkg(false);
      }
    };
    getPackageEnquiries();
  }, [accessToken, selectedDate]);

  // Fetch Followup Data
  useEffect(() => {
    const getPreFollowup = async () => {
      if (enqID) {
        try {
          const res = await fetch(
            `${API_URL}/web/previous_follow_up/1/${enqID}`,
            {
              headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
              },
            },
          );
          const data = await res.json();
          setPreFollowup(data);
        } catch (error) {
          console.error("Error fetching Previous Followup Data:", error);
        }
      }
    };
    getPreFollowup();
  }, [enqID, accessToken]);

  function findPreFollowupRecords(eveId) {
    const matchingRecords = pkgEnq.find((record) => record.eve_id === eveId);
    if (matchingRecords) {
      setEnqID(matchingRecords.eve_id);
    }
  }

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  useEffect(() => {
    setPage(0);
  }, [selectedDate, selectedService, selectedCallerStatus]);

  // Filter Logic Updated for Package JSON Structure
  const filteredData = pkgEnq.filter((item) => {
    const services = Array.isArray(item.srv_id) ? item.srv_id : [];
    const serviceDates = services
      .map((service) => service?.start_date)
      .filter(Boolean);
    const hasValidService = services.length > 0;

    // Get Package Name (checking sub_service or combining service titles from srv_id)
    const pkgName = item.sub_service
      ? item.sub_service
      : hasValidService
        ? item.srv_id.map((s) => s.srv_id?.service_title).join(", ")
        : "";

    const isDateMatch =
      selectedDate === "" ||
      serviceDates.some((startDate) => startDate.slice(0, 10) === selectedDate);
    const isServiceMatch =
      selectedService === "" ||
      (pkgName &&
        pkgName.toLowerCase().includes(selectedService.toLowerCase()));

    const isCallerStatusMatch =
      selectedCallerStatus === "" ||
      selectedCallerStatus === 5 ||
      item.patient_service_status === selectedCallerStatus;

    return isDateMatch && isServiceMatch && isCallerStatusMatch;
  });

  const formatDate = (dateString) => {
    if (!dateString || dateString === "-") return "-";
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const getEnquiryStatusTooltip = (enquiryStatus) => {
    const title =
      enquiryStatus === "1"
        ? "Follow up reschedule"
        : enquiryStatus === "2"
          ? "Cancelled Enquiry"
          : enquiryStatus === "3"
            ? "Already tried to convert into service"
            : enquiryStatus === "4"
              ? "Follow up pending"
              : "";
    const iconColor =
      enquiryStatus === "1"
        ? "#1342BA"
        : enquiryStatus === "2"
          ? "#D61616"
          : enquiryStatus === "3"
            ? "#3D8A00"
            : enquiryStatus === "4"
              ? "#D61616"
              : "#000000";
    return (
      <Tooltip title={title} arrow>
        <IconButton>
          <CircleIcon style={{ color: iconColor, fontSize: "20px" }} />
        </IconButton>
      </Tooltip>
    );
  };

  const getCallerStatusTooltip = (callerStatus) => {
    const icon =
      callerStatus === 1 ? (
        <SmartphoneIcon style={{ color: "#EB8793" }} />
      ) : callerStatus === 2 ? (
        <LanguageIcon style={{ color: "#7AB7EE" }} />
      ) : callerStatus === 3 ? (
        <DirectionsWalkOutlinedIcon style={{ color: "#84CDB1" }} />
      ) : callerStatus === 4 ? (
        <CallOutlinedIcon style={{ color: "#8D9BED" }} />
      ) : callerStatus === 5 ? (
        <DoneAllOutlinedIcon style={{ color: "#8D9BED" }} />
      ) : (
        <ErrorOutlineIcon style={{ color: "#EB8793" }} />
      );
    return <IconButton>{icon}</IconButton>;
  };

  return (
    <Box>
      <TableContainer
        sx={{
          height:
            filteredData.length === 0 || filteredData.length < 5
              ? "60vh"
              : "default",
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <EnquiryCard
                style={{
                  color: "black",
                  borderRadius: "8px 10px 0 0",
                  display: "flex",
                  width: "100%",
                  height: "50px",
                  background: "rgb(186, 241, 248)",
                }}
              >
                <CardContent
                  style={{ flex: 0.5, borderRight: "1px solid #FFFFFF" }}
                >
                  <Typography variant="subtitle2">Sr. No</Typography>
                </CardContent>
                <CardContent
                  style={{ flex: 0.3, borderRight: "1px solid #FFFFFF" }}
                >
                  <Typography variant="subtitle2">Source</Typography>
                </CardContent>
                <CardContent
                  style={{ flex: 1, borderRight: "1px solid #FFFFFF" }}
                >
                  <Typography variant="subtitle2">Event Code</Typography>
                </CardContent>
                <CardContent
                  style={{ flex: 1.2, borderRight: "1px solid #FFFFFF" }}
                >
                  <Typography variant="subtitle2">Patient Name</Typography>
                </CardContent>
                <CardContent
                  style={{ flex: 1, borderRight: "1px solid #FFFFFF" }}
                >
                  <Typography variant="subtitle2">Caller Mobile</Typography>
                </CardContent>
                <CardContent
                  style={{ flex: 1.5, borderRight: "1px solid #FFFFFF" }}
                >
                  <Typography variant="subtitle2">Package Name</Typography>
                </CardContent>
                <CardContent
                  style={{ flex: 0.8, borderRight: "1px solid #FFFFFF" }}
                >
                  <Typography variant="subtitle2">Diagnosis</Typography>
                </CardContent>
                <CardContent
                  style={{ flex: 1, borderRight: "1px solid #FFFFFF" }}
                >
                  <Typography variant="subtitle2">Start Date</Typography>
                </CardContent>
                <CardContent
                  style={{ flex: 0.8, borderRight: "1px solid #FFFFFF" }}
                >
                  <Typography variant="subtitle2">Zone</Typography>
                </CardContent>
                <CardContent
                  style={{ flex: 0.5, borderRight: "1px solid #FFFFFF" }}
                >
                  <Typography variant="subtitle2">Status</Typography>
                </CardContent>
                <CardContent style={{ flex: 1 }}>
                  <Typography variant="subtitle2">Action</Typography>
                </CardContent>
              </EnquiryCard>
            </TableRow>
          </TableHead>

          <TableBody>
            {loadingPkg ? (
              <TableRow>
                <TableCell colSpan={12} align="center">
                  <CircularProgress size={32} />
                </TableCell>
              </TableRow>
            ) : filteredData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={12} align="center">
                  <Typography variant="body2">No Data Available</Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredData
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => {
                  // Safe extraction based on new JSON
                  const hasValidService = row.srv_id && row.srv_id.length > 0;
                  const packageDisplayStr = row.sub_service
                    ? row.sub_service
                    : hasValidService
                      ? row.srv_id
                        .map((s) => s.srv_id?.service_title)
                        .join(", ")
                      : "-";
                  const startDateStr = hasValidService
                    ? row.srv_id[0].start_date
                    : "-";

                  return (
                    <TableRow key={row.eve_id}>
                      <EnquiryCard
                        sx={{
                          background: "#f1f1f1",
                          borderLeft: "4px solid transparent",
                          transition: "all 0.3s ease-in-out",
                          cursor: "pointer",
                          "&:hover": {
                            borderLeft: "4px solid rgb(162, 217, 225)",
                            borderRight: "4px solid rgb(162, 217, 225)",
                          },
                        }}
                      >
                        <CardContent style={{ flex: 0.5 }}>
                          <Typography variant="body2">
                            {index + 1 + page * rowsPerPage}
                          </Typography>
                        </CardContent>
                        <CardContent style={{ flex: 0.3 }}>
                          <Typography variant="body2">
                            {getCallerStatusTooltip(row.patient_service_status)}
                          </Typography>
                        </CardContent>
                        <CardContent
                          style={{ flex: 1, cursor: "pointer" }}
                          onClick={() => {
                            setEvntId(row.eve_id);
                            handleOpenDetails();
                          }}
                        >
                          <Typography variant="body2" sx={{ color: "black" }}>
                            {row.event_code}
                          </Typography>
                        </CardContent>
                        <CardContent style={{ flex: 1.2 }}>
                          <Typography variant="body2">
                            {row.agg_sp_pt_id ? row.agg_sp_pt_id?.name : ""}
                          </Typography>
                        </CardContent>
                        <CardContent style={{ flex: 1 }}>
                          <Typography variant="body2">
                            {row.caller_no ? row.caller_no : "-"}
                          </Typography>
                        </CardContent>
                        <CardContent style={{ flex: 1.5 }}>
                          <Tooltip
                            title={row.package_name || "-"}
                            arrow
                            placement="top-start"
                          >
                            <Typography
                              variant="body2"
                              sx={{ cursor: "pointer" }}
                            >
                              {row.package_name?.length > 15
                                ? `${row.package_name.slice(0, 15)}...`
                                : row.package_name || "-"}
                            </Typography>
                          </Tooltip>

                        </CardContent>
                        <CardContent style={{ flex: 0.8 }}>
                          <Tooltip
                            title={row.Suffered_from || "-"}
                            arrow
                            placement="top-start"
                          >
                            <Typography
                              variant="body2"
                              sx={{ cursor: "pointer" }}
                            >
                              {row.Suffered_from?.length > 10
                                ? `${row.Suffered_from.slice(0, 6)}...`
                                : row.Suffered_from || "-"}
                            </Typography>
                          </Tooltip>
                        </CardContent>
                        <CardContent style={{ flex: 1 }}>
                          <Typography variant="body2">
                            {formatDate(startDateStr)}
                          </Typography>
                        </CardContent>
                        <CardContent style={{ flex: 0.8 }}>
                          <Typography variant="body2">
                            {row.agg_sp_pt_id
                              ? row.agg_sp_pt_id.prof_zone_id?.Name
                              : "-"}
                          </Typography>
                        </CardContent>
                        <CardContent style={{ flex: 0.5 }}>
                          <Typography variant="body2">
                            {getEnquiryStatusTooltip(
                              row.follow_up ? row.follow_up : "",
                            )}
                          </Typography>
                        </CardContent>
                        <CardContent style={{ flex: 1 }}>
                          <Button
                            variant="outlined"
                            onClick={() =>
                              handleOpen(findPreFollowupRecords(row.eve_id))
                            }
                            sx={{
                              textTransform: "capitalize",
                              borderRadius: 2,
                              width: "6rem",
                              mt: 1.2,
                              fontSize: "14px",
                              fontWeight: 500,
                              borderColor: "rgb(186, 241, 248)",
                              color: "#0097A7",
                              "&:hover": {
                                borderColor: "rgb(186, 241, 248)",
                                backgroundColor: "rgb(186, 241, 248)",
                                color: "#000",
                              },
                            }}
                          >
                            Followup
                          </Button>
                        </CardContent>
                      </EnquiryCard>
                    </TableRow>
                  );
                })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Modals */}
      <Modal open={open} onClose={handleClose}>
        <Box sx={{ ...style, width: 400, borderRadius: "10px", p: 0 }}>
          <AppBar
            position="static"
            sx={{
              background: "rgb(186, 241, 248)",
              height: "3.2rem",
              borderRadius: "10px 10px 0 0",
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              px: 2,
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontSize: "16px", fontWeight: 600, color: "#000" }}
            >
              Follow Up Enquiry
            </Typography>
            <IconButton
              onClick={handleClose}
              sx={{ color: "#000", ml: "auto" }}
            >
              <CloseIcon />
            </IconButton>
          </AppBar>
          <Box sx={{ p: 2 }}>
            <PackageFollowup
              sendData={preFollowup}
              enqData={enqID}
              onClose={handleClose}
              flag={1}
            />
          </Box>
        </Box>
      </Modal>

      <Modal open={openDetails} onClose={handleCloseDetails}>
        <Box
          sx={{
            width: 600,
            maxHeight: "80vh",
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 3,
            overflowY: "auto",
            mt: 8,
            mx: "auto",
          }}
        >
          <EnqDetails
            eveID={evntId}
            open={openDetails}
            onClose={handleCloseDetails}
          />
        </Box>
      </Modal>

      <TablePagination
        sx={{ overflowY: "hidden" }}
        rowsPerPageOptions={[5, 10, 25, 100]}
        component="div"
        count={filteredData.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Box>
  );
};

export default PackageTable;
